(() => {
  if (customElements.get('three-chain-builder')) return;
  class ThreeChainBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.selected = [null, null, null];
      this.busy = false;
      this.pendingId = null;
      this.labels = JSON.parse(this.querySelector('[data-translations]').textContent);
      this.offerRetail = this.querySelector('.tcb-hero__price s').textContent.trim();
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.sizeDialog = this.querySelector('[data-size-dialog]');
      this.renderedSelection = [];
      this.slotRows = this.selected.map(() => this.make('li','tcb-slot'));
      this.querySelector('[data-slots]').replaceChildren(...this.slotRows);
      this.onCancelDialog = event => { event.preventDefault(); this.closeSizeDialog(); };
      this.sizeDialog.addEventListener('cancel',this.onCancelDialog);
      this.root = window.Shopify?.routes?.root || '/';
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.render();
      this.observer = new IntersectionObserver(([entry]) => {
        this.querySelector('[data-mobile]').classList.toggle('is-visible', entry.isIntersecting || entry.boundingClientRect.top < 0);
      });
      this.observer.observe(this.querySelector('.tcb-builder'));
    }
    disconnectedCallback() {
      this.dialogAnimation?.cancel();
      this.sizeDialog?.close();
      document.documentElement.style.removeProperty('--tcb-dialog-page-width');
      this.sizeDialog?.classList.remove('is-closing');
      this.dialogClosing = false;
      this.sizeDialog?.removeEventListener('cancel',this.onCancelDialog);
      this.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
      this.initialized = false;
    }
    money(cents, currency = this.dataset.currency || 'USD') {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency, maximumFractionDigits: 2}).format(cents / 100);
    }
    count() { return this.selected.filter(Boolean).length; }
    handleClick(event) {
      if (event.target === this.sizeDialog || event.target.closest('[data-close-size]')) {
        this.closeSizeDialog();
        return;
      }
      if (event.target.closest('[data-start]')) {
        event.preventDefault();
        this.querySelector('.tcb-builder').scrollIntoView({behavior:this.motion(),block:'start'});
        return;
      }
      const button = event.target.closest('button');
      if (!button || this.busy || this.dialogClosing) return;
      if (button.hasAttribute('data-filter')) {
        if (button.getAttribute('aria-pressed') === 'true') return;
        const scrollTop = window.scrollY;
        this.querySelectorAll('[data-filter]').forEach(filter => filter.setAttribute('aria-pressed', String(filter === button)));
        this.cards.forEach(card => { card.hidden = button.dataset.filter !== 'all' && card.dataset.finish !== button.dataset.filter; });
        window.scrollTo({top:scrollTop,behavior:'instant'});
        this.animateElement(this.querySelector('.tcb-grid'),[{opacity:.65},{opacity:1}]);
      }
      if (button.hasAttribute('data-add') || button.hasAttribute('data-configure')) this.chooseLength(button.closest('[data-card]'));
      if (button.hasAttribute('data-confirm-variant')) {
        const card = this.choosingCard;
        if (!card) return;
        card.querySelector('[data-variant-select]').value = button.dataset.confirmVariant;
        this.closeSizeDialog(() => {
          this.addCard(card);
          if (this.count() === 3) {
            const summary = this.querySelector('.tcb-summary');
            const submit = summary.querySelector('[data-submit]');
            submit.focus({preventScroll:true});
            if (matchMedia('(max-width: 749px)').matches) {
              const summaryTop = summary.getBoundingClientRect().top - (parseFloat(getComputedStyle(summary).scrollMarginTop) || 0);
              const submitBottom = submit.getBoundingClientRect().bottom;
              const bottomSpace = this.querySelector('[data-mobile]').offsetHeight + 16;
              // Keep the main button above the sticky bar on shorter screens.
              window.scrollTo({top:window.scrollY + Math.max(summaryTop,submitBottom - window.innerHeight + bottomSpace),behavior:this.motion()});
            }
          }
        });
      }
      if (button.hasAttribute('data-remove')) {
        this.selected[Number(button.dataset.remove)] = null;
        this.clearError();
        this.render();
        this.slotRows[Number(button.dataset.remove)].querySelector('button')?.focus({preventScroll:true});
      }
      if (button.hasAttribute('data-clear')) { this.selected = [null,null,null]; this.clearError(); this.render(); }
      if (button.hasAttribute('data-submit')) {
        if (this.count() === 3) this.addSetToCart();
        else this.querySelector('.tcb-filters').scrollIntoView({behavior:this.motion(),block:'start'});
      }
      if (button.hasAttribute('data-review')) this.querySelector('.tcb-summary').scrollIntoView({behavior:this.motion(),block:'center'});
      if (button.hasAttribute('data-pick')) this.querySelector('.tcb-filters').scrollIntoView({behavior:this.motion(),block:'start'});
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    chooseLength(card) {
      if (!card || this.count() === 3 || this.busy || this.sizeDialog.open || this.dialogClosing) return;
      this.dialogScroll = {top:window.scrollY,left:window.scrollX,behavior:'instant'};
      document.documentElement.style.setProperty('--tcb-dialog-page-width',`${document.body.getBoundingClientRect().width}px`);
      this.choosingCard = card;
      const guide = this.sizeDialog.querySelector('.tcb-inline-guide');
      if (guide) guide.open = false;
      const photo = card.querySelector('img');
      const image = this.make('img');
      image.src = photo.currentSrc || photo.src; image.alt = ''; image.width = 76; image.height = 76;
      const info = this.make('div');
      info.append(this.make('h3','',card.dataset.title.toLowerCase()),this.make('p','',card.dataset.finish));
      this.querySelector('[data-size-product]').replaceChildren(image,info);
      const choices = this.querySelector('[data-size-options]');
      choices.replaceChildren();
      card.querySelectorAll('option').forEach(option => {
        const button = this.make('button','',option.textContent.trim());
        button.type = 'button'; button.dataset.confirmVariant = option.value; button.disabled = option.disabled;
        choices.append(button);
      });
      this.sizeDialog.showModal();
      window.scrollTo(this.dialogScroll);
    }
    closeSizeDialog(afterClose) {
      if (!this.sizeDialog.open || this.dialogClosing) return;
      this.dialogClosing = true;
      const finish = () => {
        this.sizeDialog.close();
        document.documentElement.style.removeProperty('--tcb-dialog-page-width');
        this.sizeDialog.classList.remove('is-closing');
        this.dialogClosing = false;
        this.choosingCard = null;
        window.scrollTo(this.dialogScroll);
        afterClose?.();
      };
      if (this.motion() === 'instant' || !this.sizeDialog.animate) { finish(); return; }
      const currentStyle = getComputedStyle(this.sizeDialog);
      const startFrame = {opacity:currentStyle.opacity,transform:currentStyle.transform};
      this.sizeDialog.classList.add('is-closing');
      this.dialogAnimation = this.sizeDialog.animate([startFrame,{opacity:0,transform:'translateY(8px)'}],{duration:140,easing:'ease-out',fill:'forwards'});
      this.dialogAnimation.finished.then(() => { finish(); this.dialogAnimation.cancel(); }).catch(() => { this.dialogClosing=false; });
    }
    animateElement(element, frames) {
      if (!element?.animate || this.motion() === 'instant') return;
      element.getAnimations().forEach(animation => animation.cancel());
      element.animate(frames,{duration:180,easing:'ease-out',fill:'backwards'});
    }
    addCard(card) {
      const empty = this.selected.indexOf(null);
      if (empty < 0) { this.setError(this.labels.full); return; }
      const option = card.querySelector('[data-variant-select]').selectedOptions[0];
      if (!option || option.disabled) return;
      this.selected[empty] = {
        productId:card.dataset.productId, title:card.dataset.title,
        finish:card.dataset.finish, variantId:option.value,
        variantTitle:option.dataset.title, price:Number(option.dataset.price),
        image:card.querySelector('img').currentSrc || card.querySelector('img').src
      };
      this.clearError();
      this.render();
      this.animateElement(card.querySelector('[data-card-badge]'),[{opacity:0},{opacity:1}]);
      this.querySelector('[data-live]').textContent = `${card.dataset.title}. ${this.count()} / 3. ${this.count() === 3 ? this.labels.ready : ''}`;
    }
    make(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    }
    icon(name) { return this.querySelector(`[data-icon="${name}"]`).content.firstElementChild.cloneNode(true); }
    render() {
      const count = this.count();
      const label = this.busy ? this.labels.adding : count === 3 ? this.labels.submit : this.labels.choose.replace('[count]', 3-count);
      this.querySelectorAll('[data-count]').forEach(el => { el.textContent = `${count} / 3`; });
      this.querySelectorAll('.tcb-meter').forEach(meter => [...meter.children].forEach((step,index) => step.classList.toggle('is-filled',index < count)));
      this.querySelectorAll('[data-submit]').forEach(el => {
        const iconName = count === 3 ? 'bag' : 'plus';
        if (!el.querySelector('span')) el.replaceChildren(this.make('span'),this.icon(iconName));
        el.querySelector('span').textContent = label;
        if (el.dataset.submitIcon !== iconName) { el.querySelector('svg')?.replaceWith(this.icon(iconName)); el.dataset.submitIcon=iconName; }
        el.disabled = this.busy;
      });
      this.querySelector('[data-progress]').textContent = count === 3 ? this.labels.ready : this.labels.choose.replace('[count]',3-count);
      this.querySelector('[data-clear]').disabled = this.busy || count === 0;
      this.selected.forEach((item, index) => {
        const row = this.slotRows[index];
        if (this.renderedSelection[index] === item) {
          row.querySelector('button').disabled = this.busy;
          return;
        }
        this.renderedSelection[index] = item;
        row.classList.toggle('is-filled',Boolean(item));
        row.replaceChildren();
        if (item) {
          const image = this.make('img'); image.src = item.image; image.alt = ''; image.width = 66; image.height = 76;
          const info = this.make('div','tcb-slot__info');
          info.append(this.make('strong','',item.title.replace(' CHAIN','')),this.make('span','',`${item.finish} · ${item.variantTitle}`));
          const remove = this.make('button','tcb-slot__remove'); remove.append(this.icon('close')); remove.type = 'button'; remove.dataset.remove = String(index); remove.disabled = this.busy;
          remove.setAttribute('aria-label',`${this.labels.remove} ${item.title}, ${item.finish}, ${item.variantTitle}`);
          row.append(image,info,remove);
        } else {
          const pick = this.make('button','tcb-slot__pick'); pick.type = 'button'; pick.dataset.pick = ''; pick.disabled = this.busy;
          pick.append(this.make('span','tcb-slot__number',`0${index+1}`),this.make('span','',this.labels.slot.replace('[count]',index+1)),this.icon('plus'));
          row.append(pick);
        }
      });
      const retail = this.selected.reduce((sum,item) => sum+(item?.price || 0),0);
      // Keep both comparison prices in USD, matching the offer shown in the hero.
      this.querySelector('[data-retail]').textContent = count === 3 ? (this.dataset.currency === 'USD' ? this.money(retail, 'USD') : this.offerRetail) : '—';
      this.querySelector('[data-savings]').textContent = count === 3 && this.dataset.currency === 'USD' ? this.labels.save.replace('[amount]',this.money(Math.max(retail-9900,0))) : this.dataset.currency !== 'USD' ? this.labels.local : this.labels.emptySlot;
      this.cards.forEach(card => {
        const selectedCount = this.selected.filter(item => item?.productId === card.dataset.productId).length;
        const badge = card.querySelector('[data-card-badge]'); badge.hidden = !selectedCount;
        if (badge.dataset.selectedCount !== String(selectedCount)) { badge.replaceChildren(this.icon('check'),this.make('span','',`${selectedCount} ${this.labels.added}`)); badge.dataset.selectedCount=String(selectedCount); }
        const add = card.querySelector('[data-add]');
        const available = [...card.querySelectorAll('option')].some(option => !option.disabled);
        add.disabled = this.busy || count === 3 || !available;
        card.querySelector('[data-configure]').disabled = add.disabled;
        card.querySelector('select').disabled = this.busy;
        card.classList.toggle('is-selected', Boolean(selectedCount));
      });
      this.setAttribute('aria-busy', String(this.busy));
    }
    clearError() { const el=this.querySelector('[data-error]'); el.hidden=true; el.textContent=''; }
    setError(message) { const el=this.querySelector('[data-error]'); el.hidden=false; el.textContent=message; el.focus({preventScroll:true}); el.scrollIntoView({behavior:this.motion(),block:'center'}); }
    getLineItemProperties(bundleId,index,totalItems) {
      return {_bundle_id:bundleId,_bundle_offer:'3 Chains for $99',_bundle_item:`${index+1} of ${totalItems}`,_three_chain_offer:'3-for-99'};
    }
    async request(path, body) {
      const response = await fetch(`${this.root}${path}`, {
        method:body ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
        headers:{'Content-Type':'application/json',Accept:'application/json'},
        ...(body ? {body:JSON.stringify(body)} : {}), signal:AbortSignal.timeout(20000)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || this.labels.network);
      return data;
    }
    bundleLines(cart,id) { return cart.items.filter(item => item.properties?._bundle_id === id); }
    bundleIsValid(cart,id) {
      const lines=this.bundleLines(cart,id);
      const quantity=lines.reduce((sum,line)=>sum+line.quantity,0);
      const total=lines.reduce((sum,line)=>sum+line.final_line_price,0);
      if (quantity !== 3) return false;
      if (cart.currency === 'USD') return total === 9900;
      const offerCode = this.dataset.discountCode?.trim().toUpperCase();
      return lines.every(line => line.line_level_discount_allocations?.some(allocation => {
        const title = allocation.discount_application?.title || '';
        return title.includes('3 Chains for $99') || (offerCode && title.toUpperCase().includes(offerCode));
      }));
    }
    async removeBundle(cart,id) {
      const lines=this.bundleLines(cart,id);
      if (!lines.length) return;
      const updates=Object.fromEntries(lines.map(line=>[line.key,0]));
      await this.request('cart/update.js',{updates});
      const clean=await this.request('cart.js');
      if (this.bundleLines(clean,id).length) throw new Error(this.labels.review);
    }
    async addSetToCart() {
      if (this.busy || this.count() !== 3) return;
      this.clearError(); this.busy=true; this.render();
      let verified=false;
      try {
        if (this.pendingId) {
          const previous=await this.request('cart.js');
          if (this.bundleIsValid(previous,this.pendingId)) { verified=true; }
          else { await this.removeBundle(previous,this.pendingId); this.pendingId=null; }
        }
        if (!verified) {
          const bundleId=`three-chains-${crypto.randomUUID()}`;
          this.pendingId=bundleId;
          let addError=null;
          try {
            await this.request('cart/add.js',{items:this.selected.map((item,index)=>({id:Number(item.variantId),quantity:1,properties:this.getLineItemProperties(bundleId,index,3)}))});
          } catch(error) { addError=error; }
          let cart=await this.request('cart.js');
          const offerCode=this.dataset.discountCode?.trim();
          const previousCodes=(cart.discount_codes || []).map(discount => discount.code).filter(Boolean);
          let codeApplied=false;
          if (!addError && offerCode) {
            try {
              const codes=[...new Set([...previousCodes,offerCode])];
              cart=await this.request('cart/update.js',{discount:codes.join(',')});
              codeApplied=true;
            } catch(error) {
              await this.removeBundle(await this.request('cart.js'),bundleId);
              if (!previousCodes.some(code => code.toUpperCase() === offerCode.toUpperCase())) {
                await this.request('cart/update.js',{discount:previousCodes.join(',')});
              }
              this.pendingId=null;
              throw error;
            }
          }
          if (!this.bundleIsValid(cart,bundleId)) {
            await this.removeBundle(cart,bundleId);
            if (codeApplied && !previousCodes.some(code => code.toUpperCase() === offerCode.toUpperCase())) {
              await this.request('cart/update.js',{discount:previousCodes.join(',')});
            }
            this.pendingId=null;
            throw new Error(addError?.message || this.labels.discount);
          }
          verified=true;
        }
        this.selected=[null,null,null]; this.pendingId=null;
        this.querySelector('[data-live]').textContent=this.labels.success;
        await this.openCart();
      } catch(error) {
        this.setError(this.pendingId ? this.labels.review : (error.message || this.labels.network));
      } finally { this.busy=false; this.render(); }
    }
    async openCart() {
      if (window.customCart && typeof window.customCart.update === 'function') {
        try { await window.customCart.update(); return; } catch { window.location.assign(`${this.root}cart`); return; }
      }
      window.location.assign(`${this.root}cart`);
    }
  }
  customElements.define('three-chain-builder',ThreeChainBuilder);
})();
